import {useMemo, useCallback, forwardRef, ReactNode} from "react";
import {History, Location} from "history";
import qs from "qs";
import {matchPath} from "react-router-dom";
import {GlobalKeyPressOpts, TooltipForChild, useGlobalKeyPress} from "@cdx/common";
import {SIDEBAR_QUERYKEY} from "../features/card-panel/side-bar-info";
import {DSIconButton} from "@cdx/ds";

type ASBaseButtonProps = {
  to: string | {pathname: string; search?: string};
  active: boolean;
  variant?: "primary" | "secondary";
  history: History;
  icon: ReactNode;
  tooltip: string;
  shortcut?: null | GlobalKeyPressOpts;
  disabled: boolean;
  tooltipOptions: any;
};

const ASBaseButton = forwardRef<HTMLElement, ASBaseButtonProps>((props, ref) => {
  const {
    to,
    active,
    variant = "secondary",
    history,
    icon,
    tooltip,
    shortcut,
    disabled,
    tooltipOptions,
  } = props;
  const keyPressFn = useCallback(() => {
    history.replace(to);
  }, [history, to]);

  useGlobalKeyPress({...shortcut, fn: keyPressFn, disabled: !shortcut} as any);

  return (
    <TooltipForChild tooltip={tooltip} placement="right" {...tooltipOptions}>
      <DSIconButton
        to={to}
        active={active}
        disabled={disabled}
        icon={icon}
        variant={variant}
        ref={ref}
        customSize="actionButton"
      />
    </TooltipForChild>
  );
});

type ASPathButtonProps = {
  pathSegement: string;
  rootPath: string;
  rootUrl: string;
  location: Location;
} & Omit<ASBaseButtonProps, "to" | "active">;

export const ASPathButton = forwardRef<HTMLElement, ASPathButtonProps>((props, ref) => {
  const {pathSegement, rootPath, rootUrl, location, ...rest} = props;
  const active = matchPath(location.pathname, {path: `${rootPath}/${pathSegement}`}) !== null;
  const to = active ? rootUrl : `${rootUrl}/${pathSegement}`;
  return <ASBaseButton to={to} active={active} {...rest} ref={ref} />;
});

type ASQueryButtonProps = {
  queryVal: string;
  rootUrl: string;
  forceActive?: boolean;
  location: Location;
} & Omit<ASBaseButtonProps, "to" | "active">;
export const ASQueryButton = forwardRef<HTMLElement, ASQueryButtonProps>((props, ref) => {
  const {queryVal, location, rootUrl, forceActive, ...rest} = props;
  const {to, active} = useMemo(() => {
    const query = qs.parse(location.search, {ignoreQueryPrefix: true});
    const isActive = query[SIDEBAR_QUERYKEY] === queryVal;
    return {
      to: {
        pathname: rootUrl || "/", // revisit after https://github.com/ReactTraining/react-router/issues/6779 is resolved
        search: qs.stringify({...query, [SIDEBAR_QUERYKEY]: isActive ? undefined : queryVal}),
      },
      active: isActive,
    };
  }, [location.search, queryVal, rootUrl]);
  return <ASBaseButton to={to} active={Boolean(active || forceActive)} {...rest} ref={ref} />;
});
